import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Checkbox, DatePicker, Drawer, Empty, Form, Input, InputNumber, List, Modal, Popconfirm, Popover, Segmented, Select,
  Space, Spin, Switch,
  Table, Tabs, Tag, TimePicker, Tooltip, Typography, message, theme,
} from 'antd';
import { DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { WFMSkillsPanel } from '../components/WFMSkillsDrawer';
import { formatTS } from '../utils/time';

// Oversight > Attendance & Schedule > WFM Availability (routes/availability.py):
// a team's people, the hours they can work each weekday and when they prefer
// to work. A name opens their panel: the week, preferences and the comments
// log. WFM Skills access: View sees it, Edit changes it, Edit Manager also
// keeps the preference list.

export const WEEKDAYS = [
  { day: 1, short: 'Mon', name: 'Monday' }, { day: 2, short: 'Tue', name: 'Tuesday' },
  { day: 3, short: 'Wed', name: 'Wednesday' }, { day: 4, short: 'Thu', name: 'Thursday' },
  { day: 5, short: 'Fri', name: 'Friday' }, { day: 6, short: 'Sat', name: 'Saturday' },
  { day: 7, short: 'Sun', name: 'Sunday' },
];

// '07:00' -> '7am', '07:30' -> '7:30am'
const short = (t) => {
  if (!t) return '';
  const d = dayjs(`2000-01-01T${t}`);
  return d.format(d.minute() ? 'h:mma' : 'ha');
};
const windowOf = (d) => {
  if (!d) return null;
  if (!d.available) return 'No';
  return `${short(d.start)}–${short(d.end)}`;
};
const toTime = (t) => (t ? dayjs(`2000-01-01T${t}`) : null);

// Paycor's leave status, spaced (LeaveWithPay -> Leave With Pay), in red.
export const onLeave = (p) => (p.OnLeave
  ? <Typography.Text type="danger" style={{ fontSize: 13 }}> {p.OnLeave.replace(/([a-z])([A-Z])/g, '$1 $2')}</Typography.Text>
  : null);

export default function AvailabilityPage() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [people, setPeople] = useState([]);
  const [prefs, setPrefs] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [canConfig, setCanConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [prefFilter, setPrefFilter] = useState([]);
  const [dayFilter, setDayFilter] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Day view: a weekday (1 Monday .. 7 Sunday) by half hour; 'off' = the
  // upcoming Pre-Approved Days Off; null = the week.
  const [view, setView] = useState(null);
  const [offDays, setOffDays] = useState(90);
  const [fromHour, setFromHour] = useState(0);
  const [toHour, setToHour] = useState(24);

  const loadPrefs = useCallback(() => {
    api.get('/api/availability/preferences').then((r) => setPrefs(r.data || [])).catch(() => setPrefs([]));
  }, []);
  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then((r) => {
      setTeams(r.data || []);
      if ((r.data || []).length === 1) setTeamId(r.data[0].ID);
    }).catch(() => setTeams([]));
    loadPrefs();
  }, [loadPrefs]);

  const load = useCallback(() => {
    if (!teamId) { setPeople([]); return; }
    setLoading(true);
    api.get('/api/availability', { params: { team_id: teamId } })
      .then((r) => { setPeople(r.data.people || []); setCanEdit(!!r.data.can_edit); setCanConfig(!!r.data.can_config); })
      .catch(() => { setPeople([]); message.error('Failed to load the team.'); })
      .finally(() => setLoading(false));
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  const prefName = Object.fromEntries(prefs.map((p) => [p.ID, p.Name]));
  const jobOptions = [...new Set(people.map((p) => p.JobName).filter(Boolean))].sort().map((j) => ({ value: j, label: j }));
  const shown = people.filter((p) => (!jobs.length || jobs.includes(p.JobName))
    && (!prefFilter.length || prefFilter.some((id) => p.PreferenceIDs.includes(id)))
    && (!dayFilter.length || dayFilter.every((d) => p.Days[d]?.available)));

  const columns = [
    { title: 'Name', dataIndex: 'DisplayName', key: 'DisplayName', width: 200, fixed: 'left',
      sorter: (a, b) => a.DisplayName.localeCompare(b.DisplayName),
      render: (v, r) => <><Typography.Link onClick={() => setOpenId(r.ID)}>{v}</Typography.Link>{onLeave(r)}</> },
    { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 120, defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || '') || a.DisplayName.localeCompare(b.DisplayName),
      render: (v) => v || '—' },
    { title: 'FT/PT', dataIndex: 'EmploymentType', key: 'EmploymentType', width: 70, render: (v) => v || '—' },
    ...WEEKDAYS.map((w) => ({
      // A day's heading opens that day by half hour.
      title: <Typography.Link onClick={() => setView(w.day)}>{w.short}</Typography.Link>,
      key: `d${w.day}`, width: 130, align: 'center',
      render: (_, r) => {
        const d = r.Days[w.day];
        const txt = windowOf(d);
        if (!txt) return <Typography.Text type="secondary">—</Typography.Text>;
        const cell = d.available ? <span style={{ whiteSpace: 'nowrap' }}>{txt}</span>
          : <Typography.Text type="secondary">No</Typography.Text>;
        return d.notes ? <span title={d.notes}>{cell} *</span> : cell;
      },
    })),
    // Pre-Approved Days Off: the next one (the rest on hover).
    { title: 'Pre-Approved Days Off', key: 'dayoff', width: 170,
      sorter: (a, b) => String(a.DaysOff[0]?.From || '9').localeCompare(String(b.DaysOff[0]?.From || '9')),
      render: (_, r) => {
        const d = r.DaysOff[0];
        if (!d) return '—';
        const all = r.DaysOff.map((x) => `${offSpan(x)}${x.Note ? ` – ${x.Note}` : ''}`).join('\n');
        return <span title={all} style={{ whiteSpace: 'nowrap' }}>{offSpan(d)}{r.DaysOff.length > 1 ? ` (+${r.DaysOff.length - 1})` : ''}</span>;
      } },
    { title: 'Preferences', key: 'prefs', width: 220,
      render: (_, r) => (r.PreferenceIDs.length
        ? r.PreferenceIDs.filter((id) => prefName[id]).map((id) => <Tag key={id}>{prefName[id]}</Tag>) : '—') },
    { title: 'Comments', dataIndex: 'CommentCount', key: 'CommentCount', width: 95, align: 'right',
      sorter: (a, b) => a.CommentCount - b.CommentCount, render: (v) => v || '—' },
    { title: 'Last Changed', key: 'UpdatedAt', width: 160,
      sorter: (a, b) => String(a.UpdatedAt || '').localeCompare(String(b.UpdatedAt || '')),
      render: (_, r) => (r.UpdatedAt ? `${formatTS(r.UpdatedAt, 'MM/DD/YYYY')}${r.UpdatedByName ? ` · ${r.UpdatedByName}` : ''}` : '—') },
  ];

  const exportExcel = () => {
    const rows = shown.map((p) => ({
      Name: p.DisplayName, Job: p.JobName || '', 'FT/PT': p.EmploymentType || '',
      ...Object.fromEntries(WEEKDAYS.map((w) => [w.name, windowOf(p.Days[w.day]) || ''])),
      ...Object.fromEntries(WEEKDAYS.map((w) => [`${w.name} Notes`, p.Days[w.day]?.notes || ''])),
      Preferences: p.PreferenceIDs.map((id) => prefName[id]).filter(Boolean).join(', '),
      'Pre-Approved Days Off': p.DaysOff.map((x) => `${offSpan(x)}${x.Note ? ` (${x.Note})` : ''}`).join('; '),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Availability');
    const team = teams.find((t) => t.ID === teamId)?.Name || 'Team';
    XLSX.writeFile(wb, `Availability ${team} ${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <>
      <Space align="center" wrap style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>WFM Availability</Typography.Title>
        <Select style={{ width: 240 }} placeholder="Team" showSearch optionFilterProp="label"
          value={teamId} onChange={setTeamId} options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Select style={{ minWidth: 160 }} mode="multiple" allowClear placeholder="Job" maxTagCount="responsive"
          value={jobs} onChange={setJobs} options={jobOptions} />
        <Select style={{ minWidth: 180 }} mode="multiple" allowClear placeholder="Available on" maxTagCount="responsive"
          value={dayFilter} onChange={setDayFilter} options={WEEKDAYS.map((w) => ({ value: w.day, label: w.name }))} />
        <Select style={{ minWidth: 180 }} mode="multiple" allowClear placeholder="Preference" maxTagCount="responsive"
          value={prefFilter} onChange={setPrefFilter} options={prefs.map((p) => ({ value: p.ID, label: p.Name }))} />
        <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={!shown.length}>Export</Button>
        {canConfig && <Button icon={<SettingOutlined />} onClick={() => setPrefsOpen(true)}>Preferences</Button>}
      </Space>
      <Space align="center" wrap style={{ marginBottom: 12 }}>
        <Segmented value={view ?? 0} onChange={(v) => setView(v || null)}
          options={[{ value: 0, label: 'Week' }, ...WEEKDAYS.map((w) => ({ value: w.day, label: w.short })),
            { value: 'off', label: 'Pre-Approved Days Off' }]} />
        {view === 'off' && (
          <Select style={{ width: 150 }} value={offDays} onChange={setOffDays}
            options={[30, 60, 90, 180, 365].map((n) => ({ value: n, label: `Next ${n} days` }))} />
        )}
        {typeof view === 'number' && (
          <>
            <Select style={{ width: 110 }} value={fromHour} onChange={setFromHour}
              options={HOURS.filter((h) => h < toHour).map((h) => ({ value: h, label: hourLabel(h) }))} />
            <Typography.Text>to</Typography.Text>
            <Select style={{ width: 110 }} value={toHour} onChange={setToHour}
              options={HOURS.map((h) => h + 1).filter((h) => h > fromHour).map((h) => ({ value: h, label: hourLabel(h % 24) }))} />
          </>
        )}
      </Space>
      {!teamId ? <Empty description="Pick a team." /> : view === 'off' ? (
        <DaysOffList teamId={teamId} days={offDays} jobs={jobs} onOpen={setOpenId} />
      ) : view ? (
        <DayView day={view} people={shown} loading={loading} fromHour={fromHour} toHour={toHour} onOpen={setOpenId} />
      ) : (
        <Table rowKey="ID" size="small" loading={loading} dataSource={shown} columns={columns}
          pagination={false} sticky scroll={{ x: 'max-content' }} locale={{ emptyText: 'Nobody matches.' }} />
      )}
      <AvailabilityDrawer userId={openId} prefs={prefs} onClose={() => setOpenId(null)} onSaved={load} />
      <PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} onSaved={() => { loadPrefs(); load(); }} />
    </>
  );
}

// A Pre-Approved Day Off occurrence: 'Wed 11/11' or 'Wed 11/11 – Fri 11/13'.
const offSpan = (x) => (x.From === x.To ? dayjs(x.From).format('ddd MM/DD')
  : `${dayjs(x.From).format('ddd MM/DD')} – ${dayjs(x.To).format('ddd MM/DD')}`);

// Days Off view: the team's upcoming Pre-Approved Days Off, soonest first.
function DaysOffList({ teamId, days, jobs, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get('/api/availability/days-off', { params: { team_id: teamId, days } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [teamId, days]);
  const shown = rows.filter((r) => !jobs.length || jobs.includes(r.JobName));
  return (
    <Table rowKey={(r) => `${r.ID}|${r.From}`} size="small" loading={loading} dataSource={shown} pagination={false}
      locale={{ emptyText: 'No upcoming Pre-Approved Days Off.' }}
      columns={[
        { title: 'Date', key: 'date', width: 200, render: (_, r) => offSpan(r),
          sorter: (a, b) => a.From.localeCompare(b.From), defaultSortOrder: 'ascend' },
        { title: 'Name', dataIndex: 'Name', key: 'Name', width: 200,
          sorter: (a, b) => a.Name.localeCompare(b.Name),
          render: (v, r) => <Typography.Link onClick={() => onOpen(r.UserID)}>{v}</Typography.Link> },
        { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 120, render: (v) => v || '—' },
        { title: 'Note', dataIndex: 'Note', key: 'Note', render: (v) => v || '—' },
        { title: 'Repeats', dataIndex: 'RepeatsYearly', key: 'RepeatsYearly', width: 110,
          render: (v) => (v ? <Tag>Every year</Tag> : '') },
        { title: 'Added By', dataIndex: 'CreatedByName', key: 'CreatedByName', width: 160, render: (v) => v || '—' },
      ]} />
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = (h) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
const mins = (t) => parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(3), 10);

// Minutes of slot [a, a+30) inside a person's availability on `day`: that
// day's window, and the day before's when it runs past midnight.
function slotMinutes(p, day, a) {
  const wins = [];
  const add = (d, shift) => {
    if (!d?.available || !d.start || !d.end) return;
    const w0 = mins(d.start);
    let w1 = mins(d.end);
    if (w1 <= w0) w1 += 1440;
    wins.push([w0 + shift, w1 + shift]);
  };
  add(p.Days[day], 0);
  add(p.Days[day === 1 ? 7 : day - 1], -1440);
  return Math.min(30, wins.reduce((t, [w0, w1]) => t + Math.max(0, Math.min(a + 30, w1) - Math.max(a, w0)), 0));
}

// The day view: each person by half hour, shaded where they are available
// (lighter where only part of the half hour is), with that day's note; the
// top row counts who is available for the whole half hour.
function DayView({ day, people, loading, fromHour, toHour, onOpen }) {
  const { token } = theme.useToken();
  const slots = [];
  for (let m = fromHour * 60; m < toHour * 60; m += 30) slots.push(m);
  // Every half hour in full: 10am, 10:30am.
  const label = (m) => dayjs().startOf('day').add(m, 'minute').format(m % 60 ? 'h:mma' : 'ha');
  const counts = slots.map((a) => people.filter((p) => slotMinutes(p, day, a) >= 30).length);
  const rows = [{ ID: 'count', _count: true }, ...people];
  const columns = [
    // The name, and that day's window by it as on Week (e.g. 4pm–9pm, or No).
    { title: 'Name', key: 'name', width: 270, fixed: 'left',
      render: (_, r) => (r._count ? <Typography.Text strong>Available</Typography.Text> : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <Typography.Link onClick={() => onOpen(r.ID)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {r.DisplayName}
          </Typography.Link>
          {onLeave(r)}
          {windowOf(r.Days[day]) && (
            <Typography.Text type="secondary" style={{ marginLeft: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {windowOf(r.Days[day])}
            </Typography.Text>
          )}
        </div>
      )) },
    { title: 'Job', dataIndex: 'JobName', key: 'job', width: 110, fixed: 'left', render: (v, r) => (r._count ? '' : v || '—') },
    // Narrow: the start of the note, the rest on hover.
    { title: 'Note', key: 'note', width: 64, fixed: 'left',
      render: (_, r) => {
        const note = r._count ? '' : r.Days[day]?.notes || '';
        return note ? (
          <Tooltip title={note}>
            <span style={{ cursor: 'help' }}>{note.length > 5 ? `${note.slice(0, 5)}…` : note}</span>
          </Tooltip>
        ) : '';
      } },
    ...slots.map((a, i) => ({
      title: label(a), key: `s${a}`, width: 46, align: 'center',
      onHeaderCell: () => ({ style: { padding: '4px 0', fontSize: 11, whiteSpace: 'nowrap',
        borderLeft: a % 60 ? undefined : `1px solid ${token.colorBorder}` } }),
      onCell: (r) => {
        const style = { padding: 0, borderLeft: a % 60 ? undefined : `1px solid ${token.colorBorder}` };
        if (r._count) return { style: { ...style, fontSize: 11, fontWeight: 600 } };
        const m = slotMinutes(r, day, a);
        return { style: { ...style, background: m >= 30 ? token.colorPrimary : m > 0 ? token.colorPrimaryBgHover : undefined } };
      },
      render: (_, r) => (r._count ? counts[i] : null),
    })),
  ];
  return (
    <Table rowKey="ID" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false}
      sticky bordered scroll={{ x: 'max-content' }} locale={{ emptyText: 'Nobody matches.' }} />
  );
}

// One person's availability in a drawer (this page), with a tab for their
// WFM Skills (Save shows on the availability tab only).
export function AvailabilityDrawer({ userId, prefs, onClose, onSaved }) {
  const [tab, setTab] = useState('avail');
  useEffect(() => { setTab('avail'); }, [userId]);
  return (
    <AvailabilityPanel userId={userId} prefs={prefs} onSaved={onSaved} onLoadError={onClose}
      frame={(title, save, body) => (
        <Drawer open={!!userId} onClose={onClose} width={1000} destroyOnClose title={title}
          extra={tab === 'avail' ? save : null}>
          <Tabs activeKey={tab} onChange={setTab} items={[
            { key: 'avail', label: 'WFM Availability', children: body },
            { key: 'skills', label: 'WFM Skills', children: <WFMSkillsPanel userId={userId} /> },
          ]} />
        </Drawer>
      )} />
  );
}

// One person's availability: the week (available, earliest start, latest
// stop, a note per day), their preferences, and the comments log. `frame`
// places the title, Save and the body (a drawer); without it, Save sits
// above the body (Schedule Break & Lunch's person panel).
export function AvailabilityPanel({ userId, prefs, onSaved, onLoadError, frame }) {
  const [form] = Form.useForm();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    if (!userId) { setData(null); return; }
    api.get(`/api/availability/${userId}`).then((r) => {
      setData(r.data);
      form.setFieldsValue({
        days: WEEKDAYS.map((w) => {
          const d = r.data.Days[w.day] || {};
          return { available: !!d.available, start: toTime(d.start), end: toTime(d.end), notes: d.notes || '' };
        }),
        preference_ids: r.data.PreferenceIDs,
      });
    }).catch(() => { message.error('Failed to load availability.'); onLoadError?.(); });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const canEdit = !!data?.can_edit;
  const save = async () => {
    const v = form.getFieldsValue();
    setSaving(true);
    try {
      await api.put(`/api/availability/${userId}`, {
        days: WEEKDAYS.map((w, i) => ({
          day: w.day, available: !!v.days[i].available,
          start: v.days[i].start ? v.days[i].start.format('HH:mm') : null,
          end: v.days[i].end ? v.days[i].end.format('HH:mm') : null,
          notes: v.days[i].notes || null,
        })),
        preference_ids: v.preference_ids || [],
      });
      message.success('Availability saved.');
      onSaved?.();
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };
  const post = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/api/availability/${userId}/comments`, { comment });
      setComment('');
      onSaved?.();
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not add the comment.');
    } finally {
      setPosting(false);
    }
  };

  // Inactive preferences stay listed for someone who already has one.
  const prefOptions = prefs.map((p) => ({ value: p.ID, label: p.StartTime ? `${p.Name} (~${short(p.StartTime)})` : p.Name }));

  const title = data ? `${data.DisplayName}${data.TeamName ? ` · ${data.TeamName}` : ''}` : 'Availability';
  const saveBtn = canEdit && <Button type="primary" loading={saving} onClick={save}>Save</Button>;
  const body = (
    <>
      {!data ? <Spin /> : (
        <>
          <Form form={form} layout="vertical" disabled={!canEdit}>
            <Typography.Title level={5} style={{ marginTop: 0 }}>Availability Hours</Typography.Title>
            <Form.List name="days">
              {(fields) => (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={{ width: 100 }}>Weekday</th><th style={{ width: 80 }}>Available</th>
                      <th style={{ width: 120 }}>Start (earliest)</th><th style={{ width: 120 }}>Stop (latest)</th><th>Notes</th>
                      {canEdit && <th style={{ width: 90 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f, i) => (
                      <tr key={f.key}>
                        <td style={{ fontWeight: 600 }}>{WEEKDAYS[i].name}</td>
                        <td><Form.Item name={[f.name, 'available']} valuePropName="checked" style={{ margin: 4 }}><Switch size="small" /></Form.Item></td>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const on = getFieldValue(['days', f.name, 'available']);
                            return (
                              <>
                                <td><Form.Item name={[f.name, 'start']} style={{ margin: 4 }}>
                                  <TimePicker use12Hours format="h:mm A" minuteStep={15} showNow={false} disabled={!on || !canEdit} placeholder={on ? 'Start' : 'No'} />
                                </Form.Item></td>
                                <td><Form.Item name={[f.name, 'end']} style={{ margin: 4 }}>
                                  <TimePicker use12Hours format="h:mm A" minuteStep={15} showNow={false} disabled={!on || !canEdit} placeholder={on ? 'Stop' : 'No'} />
                                </Form.Item></td>
                              </>
                            );
                          }}
                        </Form.Item>
                        <td><Form.Item name={[f.name, 'notes']} style={{ margin: 4 }}><Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={500} /></Form.Item></td>
                        {canEdit && <td><CopyDay from={i} form={form} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Form.List>
            <Form.Item name="preference_ids" label="Preference">
              <Checkbox.Group options={prefOptions} />
            </Form.Item>
          </Form>
          {data.UpdatedAt && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Last changed {formatTS(data.UpdatedAt, 'MM/DD/YYYY h:mm A')}{data.UpdatedByName ? ` by ${data.UpdatedByName}` : ''}
            </Typography.Text>
          )}

          <DaysOffSection data={data} canEdit={canEdit} onChanged={() => { onSaved?.(); load(); }} />

          <Typography.Title level={5} style={{ marginTop: 20 }}>Comments</Typography.Title>
          {canEdit && (
            <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} value={comment}
                onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" />
              <Button type="primary" loading={posting} disabled={!comment.trim()} onClick={post}>Add</Button>
            </Space.Compact>
          )}
          <List size="small" dataSource={data.Comments} locale={{ emptyText: 'No comments yet.' }}
            renderItem={(c) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTS(c.CreatedAt, 'MM/DD/YYYY')} · {c.CreatedByName || '—'}
                  </Typography.Text>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{c.Comment}</div>
                </div>
              </List.Item>
            )} />
        </>
      )}
    </>
  );
  if (frame) return frame(title, saveBtn, body);
  return (
    <>
      {saveBtn && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>{saveBtn}</div>}
      {body}
    </>
  );
}

// Pre-Approved Days Off on a person's panel: their entries (a day or a range,
// repeating every year or not, a note) and adding or removing one.
function DaysOffSection({ data, canEdit, onChanged }) {
  const [range, setRange] = useState(null);
  const [repeats, setRepeats] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!range?.[0]) return;
    setBusy(true);
    try {
      await api.post(`/api/availability/${data.ID}/days-off`, {
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1] ? range[1].format('YYYY-MM-DD') : null,
        repeats_yearly: repeats, note,
      });
      setRange(null); setRepeats(false); setNote('');
      onChanged();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not add it.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/api/availability/days-off/${id}`);
      onChanged();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not remove it.');
    }
  };
  const fmt = (e) => {
    const f = dayjs(e.DateFrom); const t = e.DateTo ? dayjs(e.DateTo) : null;
    const pat = e.RepeatsYearly ? 'MMMM D' : 'MM/DD/YYYY';
    return t ? `${f.format(pat)} – ${t.format(pat)}` : f.format(pat);
  };
  // In a box of its own, so it is not mistaken for the hours above.
  return (
    <Card size="small" title="Pre-Approved Days Off" style={{ marginTop: 20 }}>
      <List size="small" dataSource={data.DaysOffEntries || []} locale={{ emptyText: 'None.' }}
        renderItem={(e) => (
          <List.Item actions={canEdit ? [
            <Popconfirm key="del" title="Remove this day off?" okText="Remove" okButtonProps={{ danger: true }}
              onConfirm={() => remove(e.ID)}>
              <Button size="small" type="link" danger>Remove</Button>
            </Popconfirm>,
          ] : []}>
            <div>
              <Typography.Text strong>{fmt(e)}</Typography.Text>
              {e.RepeatsYearly && <Tag style={{ marginLeft: 8 }}>Every year</Tag>}
              {e.Note && <span> – {e.Note}</span>}
            </div>
          </List.Item>
        )} />
      {canEdit && (
        <Space wrap style={{ marginTop: 8 }}>
          <DatePicker.RangePicker value={range} onChange={setRange} allowEmpty={[false, true]} format="MM/DD/YYYY" />
          <Checkbox checked={repeats} onChange={(e) => setRepeats(e.target.checked)}>Repeats every year</Checkbox>
          <Input style={{ width: 220 }} placeholder="Note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          <Button type="primary" loading={busy} disabled={!range?.[0]} onClick={add}>Add</Button>
        </Space>
      )}
    </Card>
  );
}

// Copy to...: one day's Available, Start and Stop onto the days ticked (not
// its note), with Mon-Fri and All days shortcuts.
function CopyDay({ from, form }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState([]);
  const others = WEEKDAYS.map((w, i) => ({ ...w, i })).filter((w) => w.i !== from);
  const apply = () => {
    const days = form.getFieldValue('days') || [];
    const src = days[from] || {};
    form.setFieldValue('days', days.map((d, i) => (to.includes(i)
      ? { ...d, available: !!src.available, start: src.start || null, end: src.end || null } : d)));
    setOpen(false);
    setTo([]);
  };
  const pick = (idx) => setTo(idx.filter((i) => i !== from));
  return (
    <Popover trigger="click" open={open} onOpenChange={setOpen} placement="left"
      content={(
        <div style={{ width: 170 }}>
          <Space size={4} style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => pick([0, 1, 2, 3, 4])}>Mon–Fri</Button>
            <Button size="small" onClick={() => pick([0, 1, 2, 3, 4, 5, 6])}>All days</Button>
          </Space>
          <Checkbox.Group value={to} onChange={setTo} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            options={others.map((w) => ({ value: w.i, label: w.name }))} />
          <Button type="primary" size="small" block style={{ marginTop: 8 }} disabled={!to.length} onClick={apply}>Apply</Button>
        </div>
      )}>
      <Button size="small">Copy to…</Button>
    </Popover>
  );
}

// Edit Manager: the preference list -- name, usual start, order, active.
function PreferencesModal({ open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get('/api/availability/preferences', { params: { all: 1 } })
      .then((r) => setRows((r.data || []).map((p) => ({ ...p, _start: toTime(p.StartTime) }))))
      .catch(() => setRows([]));
  }, [open]);
  const set = (id, patch) => setRows((cur) => cur.map((r) => (r.ID === id ? { ...r, ...patch } : r)));
  const save = async (r) => {
    setBusy(r.ID);
    const body = { name: r.Name, start_time: r._start ? r._start.format('HH:mm') : null, sort_order: r.SortOrder, is_active: r.IsActive };
    try {
      if (r.ID < 0) await api.post('/api/availability/preferences', body);
      else await api.put(`/api/availability/preferences/${r.ID}`, body);
      message.success('Saved.');
      onSaved?.();
      const fresh = await api.get('/api/availability/preferences', { params: { all: 1 } });
      setRows((fresh.data || []).map((p) => ({ ...p, _start: toTime(p.StartTime) })));
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setBusy(null);
    }
  };
  const add = () => setRows((cur) => [...cur, {
    ID: -Date.now(), Name: '', _start: null, IsActive: true,
    SortOrder: Math.max(0, ...cur.map((r) => r.SortOrder || 0)) + 10,
  }]);
  return (
    <Modal open={open} onCancel={onClose} footer={null} width={680} title="Preferences" destroyOnClose>
      <Table rowKey="ID" size="small" pagination={false} dataSource={rows} columns={[
        { title: 'Name', key: 'Name', render: (_, r) => <Input value={r.Name} maxLength={50} onChange={(e) => set(r.ID, { Name: e.target.value })} /> },
        { title: 'Usual Start', key: 'start', width: 140, render: (_, r) => (
          <TimePicker use12Hours format="h:mm A" minuteStep={15} showNow={false} value={r._start} onChange={(v) => set(r.ID, { _start: v })} />) },
        { title: 'Order', key: 'order', width: 90, render: (_, r) => (
          <InputNumber value={r.SortOrder} onChange={(v) => set(r.ID, { SortOrder: v ?? 0 })} style={{ width: 70 }} />) },
        { title: 'Active', key: 'active', width: 70, render: (_, r) => (
          <Switch size="small" checked={r.IsActive} onChange={(v) => set(r.ID, { IsActive: v })} />) },
        { title: '', key: 'save', width: 70, render: (_, r) => (
          <Button size="small" type="primary" loading={busy === r.ID} disabled={!r.Name.trim()} onClick={() => save(r)}>Save</Button>) },
      ]} />
      <Button style={{ marginTop: 12 }} onClick={add}>Add Preference</Button>
    </Modal>
  );
}
